import React, {
  useState, useEffect, useRef, Suspense,
} from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import clsx from 'clsx';
import useFollowAsset from 'sdk/hooks/follow/useFollowAsset';
import useAssetFollowers from 'sdk/hooks/follow/useAssetFollowers';
import { getCurrencySybol } from 'sdk/utils/currency';
import {
  formatAssetStat,
} from 'sdk/utils/formatValues';
import Button from 'components/shared/Button';
import Chart from 'components/shared/Chart';
import Page from 'modules/app/components/Page';
import { ReactComponent as IconShare } from 'images/share.svg';
// import { ReactComponent as IconInfo } from 'images/info.svg';
import { ReactComponent as IconArrowRight } from 'images/arrow-right.svg';
import { ReactComponent as IconArrowDown } from 'images/arrow-down-w.svg';
import useUser from 'sdk/hooks/user/useUser';
import useGetTradingPlatformById from 'sdk/hooks/trading/useGetTradingPlatformById';
import useHasTradingPlatform from 'sdk/hooks/trading/useHasTradingPlatform';
import TradingModal from 'modules/assets/components/TradingModal/TradingModal';
import CompareTradingModal
  from
  'modules/assets/components/CompareTradingModal/CompareTradingModal';
import useAddUserTrading from 'sdk/hooks/trading/useAddUserTrading';
import useUpdateUserTrading from 'sdk/hooks/trading/useUpdateUserTrading';

import ShareModal from 'modules/feed/components/Feed/ShareModal';
import MessageModal from 'components/shared/modals/MessageModal';
import useGetAssetIdByName from 'sdk/hooks/assets/useGetAssetIdByName';
import useGetAssetStatsVideo from 'sdk/hooks/assets/useGetAssetStatsVideo';
import Modal from 'components/shared/modals/Modal';
import Video from 'modules/feed/components/Feed/Post/Video';
import Loader from 'components/shared/Loader/Loader';

import useIsAuth from 'sdk/hooks/useIsAuth';
import SignIn from 'modules/auth/components/SignIn';
import PassReset from 'modules/auth/components/PassReset';
import SignUp from 'modules/auth/components/SignUp';
import { useSessionStorage } from 'usehooks-ts';
import PageContainer from 'components/shared/PageContainer';
import pageContainerStyles from 'components/shared/PageContainer/PageContainer.module.scss';
import { getTradingUrl } from 'sdk/utils/assets';
import styles from './Asset.module.scss';
import { useAssetChartData } from '../../../../sdk/hooks/assets/useAssetChartData';
import AssetSkeleton from './AssetSkeleton';
// import AssetInfo from './AssetInfo';

const MemoizedChart = React.memo(Chart);
const AssetDiscoveryLazy = React.lazy(() => import('./AssetInfo'));
function Asset() {
  const navigate = useNavigate()
  const userData = useUser()
  const { id: paramsId, name, category } = useParams()
  const nameId = useGetAssetIdByName(name)
  const id = (paramsId ?? nameId.data)

  const {
    isPressIn,
    setPressIn,
    lastPrice,
    period,
    setPeriod,
    selectedPrice,
    selectPriceHandler,
    assetData,
    periods,
    oldPrice,
    setIsPreMarket,
    isAfterHours,
    setIsAfterHours,
    delta,
    afterHoursDelta,
    showDays,
    isYear,
    showTime,
    assetEventData,
    assetEventsTotalLength,
    getTopText,
    getBottomText,
    isETF,
    isCurrency,
    isFall,
    isOpenMarket,
    assetEventDataForDay,
    isCrypto,
  } = useAssetChartData(id)

  const { data: isAuthData } = useIsAuth()
  const isAuth = isAuthData?.isAuth
  const [isSignInVisible, setSignInVisible] = useState(false)
  const [passResetVisiblePage, setPassResetVisiblePage] = useState('')
  const [isSignUpVisible, setSignUpVisible] = useState(false)
  const [, setSignUpClosed] = useSessionStorage<any>('signUpClosed', null)
  const [signInInitialStep, setSignInInitialStep] = useState('')

  const [isFollowed, setIsFollowed] = useState(false)
  const [isOpenTradingModal, setIsOpenTradingModal] = useState(false)
  const [isOpenCompareModal, setIsOpenCompareModal] = useState(false)
  const [isPlatformPopup, setIsPlatformPopup] = useState<string | boolean>(false)
  const {
    data: hasTradingPlatform,
    refetch: refetchTrade,
  } = useHasTradingPlatform(
    userData.data?.user.id as string,
    assetData?.category?.id as string,
    id as string,
  )
  const {
    data: selectedTrading,
    refetch,
  } = useGetTradingPlatformById(hasTradingPlatform?.trading_platform_id as number)
  const stickyHeaderRef = useRef<HTMLDivElement>(null);
  const [selectedTradingTmp, setSelectedTradingTmp] = useState<any>(null)
  // const { mutate: getStatVideo } = useGetAssetStatsVideo()

  useEffect(() => {
    const el = stickyHeaderRef?.current;
    const observer = new IntersectionObserver(
      ([e]) => e.target.classList.toggle(styles.infoHeader_isPinned, e.intersectionRatio < 1),
      { threshold: [1] },
    );
    if (el) {
      observer.observe(el);
    }
  }, [stickyHeaderRef?.current])

  useEffect(() => {
    if (hasTradingPlatform) {
      refetch()
    }
  }, [hasTradingPlatform])

  const onOpenTradingModal = () => {
    setIsOpenTradingModal(true)
  }

  const onCloseTradingModal = () => {
    setIsOpenTradingModal(false)
  }

  const onOpenCompareModal = () => {
    setIsOpenCompareModal(true)
  }

  const onCloseCompareModal = () => {
    setIsOpenCompareModal(false)
  }

  const assetFollowersData = useAssetFollowers(id)
  const followingMutation = useFollowAsset(id)
  const [isShowShareModal, setIsShowShareModal] = useState(false)

  useEffect(() => {
    setIsFollowed(Boolean(assetFollowersData?.data?.isFollowedByMe))
  }, [assetFollowersData?.data?.isFollowedByMe])

  const [isOpenFullBgVideo, setIsOpenFullBgVideo] = useState(false)
  const [videoMuted, setVideoMuted] = useState(true);
  const [videoProgressHovered, setVideoProgressHovered] = useState(false)
  const { data: statVideo } = useGetAssetStatsVideo()

  const handleMuted = (value: boolean | ((prevState: boolean) => boolean)) => {
    setVideoMuted(value)
  }

  const handleVideoProgressHover = (state: boolean) => {
    setVideoProgressHovered(state);
  }

  const addTrading = useAddUserTrading(
    userData.data?.user.id as string,
    assetData?.category?.id as number,
  )
  const updateTrading = useUpdateUserTrading(
    userData.data?.user.id as string,
    assetData?.category?.id as number,
  )

  const selectTrading = (focusedId: number, link: string) => {
    if (!hasTradingPlatform) {
      setIsPlatformPopup(link)
    }
    setIsPlatformPopup(link)

    if (selectedTrading?.id) {
      updateTrading.mutate(focusedId, {
        onSuccess() {
          refetchTrade()
          onCloseTradingModal()
        },
      })
    } else {
      addTrading.mutate(focusedId, {
        onSuccess() {
          refetchTrade()
          onCloseTradingModal()
        },
      })
    }
  }
  const shouldRenderChart = assetEventData?.data != null;
  const chartData: any[] = shouldRenderChart ? assetEventData?.data ?? [] : [];

  return (
    assetEventDataForDay.data
      ? (
        <PageContainer className={clsx(
          styles.container,
          (isFall && !assetEventData.isLoading) && styles.container_fall,
          (!isFall && !assetEventData.isLoading) && styles.container_up,
        )}
        >
          {
          isPlatformPopup && (
            <MessageModal
              title="Successfully"
              status="success"
              onClick={() => setIsPlatformPopup(false)}
              message="Continue on Eyeb or go to the trading platform?"
              buttons={[
                (<Button onClick={() => setIsPlatformPopup(false)} wide>Continue</Button>),
                (
                  <Button
                    secondary
                    wide
                    className={styles.btnModal}
                    onClick={() => {
                      window.open(getTradingUrl(selectedTradingTmp, assetData, isETF) || getTradingUrl(selectedTrading, assetData, isETF) || '/404', '_blank')
                    }}
                  >
                    <a
                      target="_blank" // @ts-ignore
                      href={getTradingUrl(selectedTradingTmp, assetData, isETF) || getTradingUrl(selectedTrading, assetData, isETF) || '/404'}
                      rel="noreferrer"
                    >
                      Go to platform
                    </a>
                  </Button>
                ),
              ]}
            />
          )
        }
          <div className={clsx(styles.containerInner)}>
            <h1 className={styles.backLink} onClick={() => navigate(-1)}>
              <IconArrowRight />
              {assetData?.ticker}
            </h1>
            <IconShare className={styles.btnShare} onClick={() => setIsShowShareModal(true)} />
            <ShareModal
              isOpen={isShowShareModal}
              onClose={() => setIsShowShareModal(false)}
            />
            <div className={styles.catIcon}>
              {assetData?.icon ? (<img src={assetData?.icon} alt={assetData?.name} />) : null}
            </div>

            <div className={styles.name}>
              {assetData?.name}
            </div>

            <div className={styles.value}>
              {`${getCurrencySybol(assetData?.currency_name)} ${formatAssetStat(isPressIn ? selectedPrice?.price : lastPrice)}`}
            </div>

            <div className={styles.percentContainer}>
              {!(isPressIn && isAfterHours) || (period !== '1D') ? (
                <div className={clsx(styles.percent, (delta < 0) && styles.percentRed)}>
                  {getTopText()}
                </div>
              ) : null}
              <div className={clsx(
                styles.percent,
                styles.afterHoursPercent,
                (((isOpenMarket || isPressIn)
                  ? afterHoursDelta : assetData?.quote?.extended_change) < 0) && styles.percentRed,
              )}
              >
                {getBottomText()}
              </div>
            </div>
          </div>

          {shouldRenderChart && (
          <div className={clsx(
            styles.chartContainer,
            pageContainerStyles.removePaddings,
          )}
          >
            <MemoizedChart
              className={styles.chart}
              data={chartData}
              showDays={showDays}
              showTime={showTime}
              isYear={isYear}
              curValue={period !== '24H' ? oldPrice : assetData?.quote?.previous_close}
              setMouseIn={setPressIn}
              setIsPremarket={setIsPreMarket}
              setIsAfterHours={setIsAfterHours}
              onSelect={(v) => selectPriceHandler(v)}
              fall={isFall}
              isCurrency={isCurrency}
              fullSize
              isShortAsset={assetData?.is_short_asset}
              isEtf={isETF}
              chartDotsCount={assetEventsTotalLength}
              isCrypto={isCrypto}
            />
          </div>
          )}
          <div className={clsx(styles.containerInner)}>
            <div className={styles.graphActions}>
              <div className={styles.graphFilters}>
                {periods?.map((item: string) => (
                  <div
                    key={item}
                    className={clsx(styles.graphFilter, (period === item)
                    && styles.graphFilterActive)}
                    onClick={() => setPeriod(item)}
                  >
                    {item}
                  </div>
                ))}
              </div>
            </div>

            <div className={styles.infoContainer}>
              <div className={styles.infoHeader} ref={stickyHeaderRef}>
                <div className={styles.infoHeaderLeft}>
                  <div className={styles.backLink} onClick={() => navigate(-1)}>
                    <IconArrowRight />
                  </div>
                  <div className={styles.catIcon}>
                    {assetData?.icon ? (<img src={assetData?.icon} alt={assetData?.name} />) : null}
                  </div>
                  <div className={styles.infoHeaderPrice}>
                    <div className={styles.value}>
                      {`${getCurrencySybol(assetData?.currency_name)} ${formatAssetStat(isPressIn ? selectedPrice?.price : lastPrice)}`}
                    </div>
                    <div className={clsx(styles.percent, (delta < 0) && styles.percentRed)}>
                      {getTopText()}
                    </div>
                  </div>
                </div>
                <div className={styles.btnGroup}>
                  {isAuth ? (
                    <Button
                      className={styles.followBtn}
                      onClick={() => {
                        followingMutation.mutate(!isFollowed)
                        setIsFollowed((v: boolean) => !v)
                      }}
                      wide
                    >
                      {isFollowed ? 'Following' : 'Follow'}
                    </Button>
                  ) : (
                    <Button
                      className={styles.followBtn}
                      onClick={() => setSignInVisible(true)}
                      wide
                    >
                      {isFollowed ? 'Following' : 'Follow'}
                    </Button>
                  )}
                  {isSignUpVisible ? (
                    <SignUp
                      onClose={() => {
                        setSignUpVisible(false)
                        setSignUpClosed(true)
                      }}
                      goToSignIn={() => {
                        setSignUpVisible(false)
                        setSignInVisible(true)
                      }}
                    />
                  ) : null}
             
                  {passResetVisiblePage ? (
                    <PassReset
                      onClose={() => setPassResetVisiblePage('')}
                      goToSignIn={() => {
                        setPassResetVisiblePage('')
                        setSignInInitialStep(passResetVisiblePage)
                        setSignInVisible(true)
                      }}
                      defaultView={passResetVisiblePage}
                    />
                  ) : null}
                  {
                  (hasTradingPlatform && selectedTrading) ? (
                    <div
                      className={styles.tradeBtnBlock}
                    >
                      <a target="_blank" href={getTradingUrl(selectedTradingTmp, assetData, isETF) || getTradingUrl(selectedTrading, assetData, isETF)} rel="noreferrer">
                        { selectedTradingTmp?.name || selectedTrading.name }
                      </a>
                      <button type="button" className={styles.tradeBtnBlockArrow} onClick={onOpenTradingModal}>
                        <IconArrowDown />
                      </button>
                    </div>
                  ) : (
                    <Button
                      className={styles.tradeBtn}
                      secondary
                      onClick={() => {
                        if (isAuth) {
                          onOpenTradingModal()
                        } else {
                          setSignInVisible(true)
                        }
                      }}
                      loading={addTrading.isLoading
                        || updateTrading.isLoading}
                    >
                      Trade
                    </Button>
                  )
                }
                </div>
              </div>
              {/* Discovery */}
              <Suspense>
                <AssetDiscoveryLazy
                  assetEventData={assetEventData}
                  assetData={assetData}
                  id={id}
                  setIsOpenFullBgVideo={setIsOpenFullBgVideo}
                  periods={periods}
                  category={category}
                  isOpenMarket={isOpenMarket}
                />
              </Suspense>

            </div>
          </div>
          <TradingModal
            categoryId={assetData?.category?.id}
            assetId={assetData?.id}
            isOpen={isOpenTradingModal}
            onClose={onCloseTradingModal}
            onClick={(trade: any) => {
              setSelectedTradingTmp(trade)
              selectTrading(trade.id, trade.website)
            }}
            onOpenCompareModal={() => {
              onCloseTradingModal()
              onOpenCompareModal()
            }}
          />
          <CompareTradingModal
            isOpen={isOpenCompareModal}
            onClose={onCloseCompareModal}
            onBack={() => {
              onCloseCompareModal()
              onOpenTradingModal()
            }}
          />
          <Modal
            onClose={() => setIsOpenFullBgVideo(false)}
            isOpen={isOpenFullBgVideo}
            onClick={(e: any) => e.stopPropagation()}
            className={styles.fullBgVideoModal}
          >
            {
            !statVideo?.video ? (
              <Loader />
            ) : (
              <Video
                src={statVideo?.video}
                handleVideoMuted={handleMuted}
                isActiveSlide
                onProgressHover={handleVideoProgressHover}
                progressHovered={videoProgressHovered}
                className={styles.video}
              />
            )
          }

          </Modal>
        </PageContainer>
      )
      : <AssetSkeleton periods={periods} />
  )
}

function AssetPage() {
  return (
    <Page activeLink="/">
      <Asset />
    </Page>
  )
}

export default AssetPage
